/**
 * SettingsTabsController — onglets de `parametres.html` (EPIC-004 / 4.6).
 *
 * Externalise la logique `maquettes/shared/tabs.js` vers un controller
 * Stimulus pour la production. Le markup HTML reste verbatim (mêmes
 * `data-tab`, `data-panel`, classes `nm-tabs`, attributs ARIA).
 *
 * Comportement :
 *   - Active le panneau dont `data-panel` matche le `data-tab` cliqué.
 *   - Synchronise `aria-selected`, `tabindex`, et `panel.hidden`.
 *   - Persiste l'onglet actif dans `location.hash` (sans reload).
 *   - Navigation clavier ←/→/Home/End sur les `[role="tab"]`.
 *
 * À noter : on ne touche PAS au markup, on lit `data-tab` / `data-panel`
 * pour identifier les correspondances. Le premier onglet est actif par
 * défaut, sauf si `location.hash` correspond à un `data-tab` existant.
 */

import BaseController from "./base_controller.js";

/**
 * Fragments d'URL spéciaux qui pointent vers un bloc d'édition d'un onglet
 * et déclenchent l'ouverture du form + le focus d'un champ précis (§99.35).
 * La résolution `fragment → tab` est faite ici ; l'ouverture du form +
 * focus du champ est gérée par le controller du panel concerné (qui lit
 * `window.location.hash` après son hydratation).
 */
const FOCUS_FRAGMENT_TO_TAB = {
  "block-identite-rcs-focus": "identite-legale",
  "block-identite-rm-focus": "identite-legale",
};

export default class SettingsTabsController extends BaseController {
  static targets = ["tab", "panel"];

  connect() {
    const fromHash = window.location.hash ? window.location.hash.slice(1) : null;
    const initial = this._resolveTab(fromHash) ?? (this.tabTargets[0]?.dataset.tab ?? null);
    if (initial) this._activate(initial, { updateHash: false });

    this._onHashChange = () => {
      const key = window.location.hash ? window.location.hash.slice(1) : null;
      const resolved = this._resolveTab(key);
      if (resolved) this._activate(resolved, { updateHash: false });
    };
    window.addEventListener("hashchange", this._onHashChange);
  }

  _resolveTab(key) {
    if (!key) return null;
    if (this._hasTab(key)) return key;
    if (FOCUS_FRAGMENT_TO_TAB[key] && this._hasTab(FOCUS_FRAGMENT_TO_TAB[key])) {
      return FOCUS_FRAGMENT_TO_TAB[key];
    }
    return null;
  }

  disconnect() {
    if (this._onHashChange) {
      window.removeEventListener("hashchange", this._onHashChange);
    }
  }

  switchTab(event) {
    const tab = event.currentTarget;
    if (!tab || !tab.dataset || !tab.dataset.tab) return;
    event.preventDefault();
    this._activate(tab.dataset.tab, { updateHash: true });
  }

  handleKey(event) {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    const tabs = this.tabTargets;
    const current = tabs.find(t => t.getAttribute("aria-selected") === "true");
    if (!current) return;
    const idx = tabs.indexOf(current);
    let nextIdx = idx;
    if (event.key === "ArrowLeft")  nextIdx = (idx - 1 + tabs.length) % tabs.length;
    else if (event.key === "ArrowRight") nextIdx = (idx + 1) % tabs.length;
    else if (event.key === "Home") nextIdx = 0;
    else if (event.key === "End")  nextIdx = tabs.length - 1;
    event.preventDefault();
    const nextKey = tabs[nextIdx]?.dataset.tab;
    if (nextKey) {
      this._activate(nextKey, { updateHash: true, focus: true });
    }
  }

  _activate(key, opts) {
    let activated = false;
    this.tabTargets.forEach(tab => {
      const isActive = tab.dataset.tab === key;
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      tab.tabIndex = isActive ? 0 : -1;
      if (isActive) activated = true;
    });
    this.panelTargets.forEach(panel => {
      panel.hidden = panel.dataset.panel !== key;
    });

    if (activated && opts?.updateHash) {
      const newHash = "#" + key;
      if (window.location.hash !== newHash) {
        try {
          window.history.replaceState(null, "", newHash);
        } catch (_) {
          window.location.hash = newHash;
        }
      }
    }
    if (activated && opts?.focus) {
      const active = this.tabTargets.find(t => t.dataset.tab === key);
      if (active) active.focus();
    }
  }

  _hasTab(key) {
    return this.tabTargets.some(t => t.dataset.tab === key);
  }
}
